/**
 * printerService — single entry point for all print operations.
 *
 * Orders, autoPrint, PrintQueueBadge and PrinterSettings all call THIS
 * service. They never talk to QZ Tray or window.AndroidPrinter directly.
 *
 * The service dispatches to the correct adapter based on the printer_mode
 * saved in admin settings ("network" or "android_builtin").
 *
 * Adding a new adapter later (e.g. Bluetooth thermal, Cloud print, another
 * POS SDK) requires only:
 *   1. A new adapter file that satisfies the AdapterContract below.
 *   2. Registering it in the `ADAPTERS` map.
 *   3. Adding the mode to the backend allowlist in
 *      /app/backend/server.py (save_printer_settings).
 *   4. Adding the mode option to the Admin Printer Settings UI.
 *
 * No changes to Orders, autoPrint, or PrintQueueBadge are ever required.
 *
 * ============================================================================
 * AdapterContract
 * ============================================================================
 *   mode                  : string — matches the settings value.
 *   isAvailable()         : Promise<boolean>
 *   getStatus()           : Promise<{available, deviceName, error?}>
 *   printReceipt(order,o) : Promise<{success, error?}>
 *   testPrint(o?)         : Promise<{success, error?}>
 */

import networkPrinterAdapter from './networkPrinterAdapter';
import androidPosPrinterAdapter from './androidPosPrinterAdapter';

// -----------------------------------------------------------------------------
// Adapter registry
// -----------------------------------------------------------------------------
const ADAPTERS = {
  network: networkPrinterAdapter,
  android_builtin: androidPosPrinterAdapter,
};

// -----------------------------------------------------------------------------
// HARD-WIRED to the Android built-in POS printer.
// -----------------------------------------------------------------------------
// QZ Tray / network printing has been retired from the customer-facing flow
// to eliminate an entire class of mode-routing bugs. The network adapter is
// kept in the code for possible future use, but every printOrder() call goes
// through the Android built-in adapter regardless of settings or localStorage.
//
// If you ever need to re-enable network mode:
//   1. Change DEFAULT_MODE back to 'network'
//   2. Remove the FORCED_MODE branch in getMode() below
//   3. Re-expose the Printer Type selector in AdminPrinterSettings
// -----------------------------------------------------------------------------
const FORCED_MODE = 'android_builtin';
const DEFAULT_MODE = 'android_builtin';

const STORAGE_KEY = 'printer_mode_v1';
let currentMode = null;

const writeStoredMode = (mode) => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (err) {
    // localStorage blocked (private mode / disabled) — in-memory value still works.
    if (process.env.NODE_ENV !== 'production') console.debug('printerService.writeStoredMode:', err);
  }
};

// Always returns the forced mode. Ignores localStorage / callers passing
// stale values — the goal is that printOrder() is unaffected by ANY prior
// mode-routing state on the device.
export const getMode = () => FORCED_MODE;

export const setMode = (_mode) => {
  // Kept as a no-op so old callers (AdminPrinterSettings, NotificationCenter,
  // AdminOrders.loadPrinterSettings) don't need to be refactored. We still
  // write the forced mode into localStorage so any component that reads it
  // directly (legacy code) sees the correct value.
  currentMode = FORCED_MODE;
  writeStoredMode(FORCED_MODE);
};

const currentAdapter = () => ADAPTERS[FORCED_MODE];

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------
export const isAvailable = () => currentAdapter().isAvailable();

export const getStatus = async () => {
  const adapter = currentAdapter();
  const status = await adapter.getStatus();
  return { ...status, mode: adapter.mode };
};

/**
 * Probe an arbitrary adapter's status WITHOUT changing the active mode.
 * Used by AdminPrinterSettings so switching between mode cards in the UI
 * doesn't accidentally reroute in-flight auto-prints.
 */
export const getStatusForMode = async (mode) => {
  const adapter = ADAPTERS[mode];
  if (!adapter) return { available: false, deviceName: null, error: `Unknown printer mode: ${mode}`, mode };
  const status = await adapter.getStatus();
  return { ...status, mode: adapter.mode };
};

/**
 * Print an order receipt through the active adapter.
 * Always resolves — never throws — with { success, error? }.
 * Preserves the SAME behavioural contract for both adapters:
 *   - Returns success:true only after the printer confirms.
 *   - Returns success:false + error string on any failure. Caller decides
 *     whether to enqueue for retry, mark FAILED, alert staff, etc.
 */
export const printOrder = async (order, opts = {}) => {
  return currentAdapter().printReceipt(order, opts);
};

/**
 * Print a canned test receipt via the active adapter. Handy for one-tap
 * validation from Printer Settings and from the PrintQueueBadge.
 */
export const testPrint = async (opts = {}) => {
  return currentAdapter().testPrint(opts);
};

/**
 * Print through a specific adapter — bypasses currentMode.
 * NOTE: while the app is hard-wired to Android built-in mode (see FORCED_MODE),
 * this function IGNORES the `mode` argument and always uses the forced
 * adapter. Kept as a stable API so callers (autoPrint, PrintQueueBadge,
 * AdminPrinterSettings Test Print) don't need to change.
 */
export const printOrderWithMode = async (_mode, order, opts = {}) => {
  return ADAPTERS[FORCED_MODE].printReceipt(order, opts);
};

export const testPrintWithMode = async (_mode, opts = {}) => {
  return ADAPTERS[FORCED_MODE].testPrint(opts);
};

export const getAvailableModes = () => Object.keys(ADAPTERS);

export default {
  getMode,
  setMode,
  isAvailable,
  getStatus,
  getStatusForMode,
  printOrder,
  testPrint,
  printOrderWithMode,
  testPrintWithMode,
  getAvailableModes,
};
